<template>
  <div class="rounded-md border p-6">
    <div class="mb-4 flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold">Risk Assessment</h3>
        <p class="text-sm text-muted-foreground">Critical abort rules evaluation</p>
      </div>
      <div v-if="loading" class="flex items-center gap-2">
        <svg
          class="h-4 w-4 animate-spin text-muted-foreground"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    </div>

    <div v-if="error" class="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
      {{ error }}
    </div>

    <div v-else-if="risk" class="space-y-4">
      <!-- Status Badge -->
      <div
        class="flex items-center justify-between rounded-md p-4"
        :class="
          risk.abort
            ? risk.severity === 'critical'
              ? 'bg-red-500/10 border border-red-500/20'
              : 'bg-yellow-500/10 border border-yellow-500/20'
            : 'bg-green-500/10 border border-green-500/20'
        "
      >
        <div class="flex items-center gap-3">
          <div
            class="h-3 w-3 rounded-full"
            :class="
              risk.abort
                ? risk.severity === 'critical'
                  ? 'bg-red-500'
                  : 'bg-yellow-500'
                : 'bg-green-500'
            "
          ></div>
          <div>
            <p class="font-semibold">
              {{ risk.abort ? (risk.severity === 'critical' ? 'ABORT' : 'WARN') : 'SAFE' }}
            </p>
            <p class="text-xs text-muted-foreground">
              {{ risk.severity === 'critical' ? 'Critical risk detected' : risk.abort ? 'Warning signs present' : 'No significant risks' }}
            </p>
          </div>
        </div>
      </div>

      <!-- Risk Reasons -->
      <div v-if="risk.reasons.length > 0" class="space-y-2">
        <p class="text-sm font-medium text-muted-foreground">Risk Triggers:</p>
        <ul class="space-y-1">
          <li
            v-for="reason in risk.reasons"
            :key="reason"
            class="flex items-center gap-2 text-sm"
          >
            <svg
              class="h-4 w-4 text-destructive"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span class="capitalize">{{ reason.replace('_', ' ') }}</span>
          </li>
        </ul>
      </div>

      <!-- Severity Info -->
      <div class="rounded-md bg-muted/50 p-3">
        <div class="flex items-center justify-between text-sm">
          <span class="text-muted-foreground">Severity</span>
          <span
            class="font-medium capitalize"
            :class="
              risk.severity === 'critical'
                ? 'text-red-500'
                : risk.severity === 'warn'
                ? 'text-yellow-500'
                : 'text-muted-foreground'
            "
          >
            {{ risk.severity }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onUnmounted } from 'vue'
import { apiClient, type RiskResponse, type RiskJobStatusResponse } from '@/lib/api'

interface Props {
  symbol: string
}

const props = defineProps<Props>()

const risk = ref<RiskResponse | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
let pollInterval: ReturnType<typeof setInterval> | null = null

const pollJobStatus = async (jobId: string) => {
  if (pollInterval) {
    clearInterval(pollInterval)
  }
  
  pollInterval = setInterval(async () => {
    try {
      const status = await apiClient.getRiskJobStatus(jobId)
      
      if (status.status === 'completed' && status.result) {
        risk.value = status.result
        loading.value = false
        if (pollInterval) {
          clearInterval(pollInterval)
          pollInterval = null
        }
      } else if (status.status === 'failed') {
        error.value = status.error || 'Risk assessment job failed'
        loading.value = false
        if (pollInterval) {
          clearInterval(pollInterval)
          pollInterval = null
        }
      }
    } catch (err: any) {
      console.error('Error polling risk job status:', err)
      if (pollInterval) {
        clearInterval(pollInterval)
        pollInterval = null
      }
      loading.value = false
      error.value = 'Failed to get job status'
    }
  }, 1000) // Poll every second for quick jobs
}

const fetchRisk = async () => {
  loading.value = true
  error.value = null
  risk.value = null
  
  try {
    // Start background job
    const jobResponse = await apiClient.startRiskJob(props.symbol)
    
    // Poll for completion
    await pollJobStatus(jobResponse.job_id)
  } catch (err: any) {
    error.value = err.response?.data?.detail || err.message || 'Failed to start risk assessment job'
    loading.value = false
    console.error('Risk assessment error:', err)
  }
}

onMounted(() => {
  fetchRisk()
})

watch(() => props.symbol, () => {
  fetchRisk()
})

onUnmounted(() => {
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
})
</script>

